import { motion, AnimatePresence } from "motion/react";
import type { Video } from "@/types/video";

// The signature element: VFD-style channel display.
// Shows CH 037, year/category, and "SIGNAL OK" status.
// Inspired by vacuum fluorescent displays from 1980s A/V receivers.

interface ChannelDisplayProps {
  video: Video | null;
  loading?: boolean;
  switching?: boolean;
  unknownMode?: boolean;
  mode?: string;
  year?: number | null;
}

function pad3(n: number): string {
  return String(n).padStart(3, "0");
}

export default function ChannelDisplay({
  video,
  loading,
  switching,
  unknownMode,
  mode,
  year,
}: ChannelDisplayProps) {
  const displayYear = year ?? video?.year ?? null;
  const displayCategory = video?.category ?? null;
  const displayChannel = video?.channel ?? null;

  return (
    <div className="font-mono select-none">
      <AnimatePresence mode="wait">
        {(loading || switching) && (
          <motion.div
            key="status-loading"
            className="vfd text-sm sm:text-base"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {switching ? "TUNING..." : "SEARCHING SIGNAL"}
            <span className="animate-pulse">...</span>
          </motion.div>
        )}
        {!loading && !switching && video && (
          <motion.div
            key={`status-${video.bvid}`}
            className="space-y-1"
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
          >
            <div className="flex items-baseline gap-3">
              <span className="vfd text-base sm:text-lg tracking-widest2">
                CH {pad3(displayChannel ?? 0)}
              </span>
              {mode && mode !== "random" && (
                <span className="text-[10px] uppercase tracking-widest2 text-static">
                  [{mode}]
                </span>
              )}
            </div>
            <div className="channel-title-marquee text-xs text-[#fff8fd]" title={video.title}>
              <motion.span
                animate={video.title.length > 24 ? { x: ["0%", "-50%"] } : { x: 0 }}
                transition={video.title.length > 24 ? { duration: Math.max(7, video.title.length * 0.18), repeat: Infinity, repeatType: "loop", ease: "linear", repeatDelay: 1.2 } : { duration: 0 }}
              >
                {video.title}{video.title.length > 24 ? `　　${video.title}` : ""}
              </motion.span>
            </div>
            <div className="text-[10px] tracking-wide text-[#f4cde2]">AV {video.aid ?? "--"}</div>
            {!unknownMode && (
              <div className="text-[10px] sm:text-xs tracking-widest2 text-static space-x-2">
                {displayYear && <span>{displayYear}</span>}
                {displayCategory && (
                  <span>/ {displayCategory.toUpperCase()}</span>
                )}
              </div>
            )}
            <div className="text-[10px] tracking-widest2 text-signal signal-strong">
              <span className="inline-block">● </span>
              SIGNAL OK
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
